import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../auth/store.ts';
import { useMyRoles } from '../api/hooks/useVisitors.ts';

/**
 * Persists the home-page Role selector per-user in localStorage and
 * rehydrates it on the next reload or sign-in. Returns `[role, setRole]`
 * the same way `useState` does.
 *
 * Behaviour:
 *   - On mount, reads the stored role for the current user (keyed by login)
 *     and uses it as the initial value.
 *   - Once /api/roles/currentUser resolves, falls back to the user's first
 *     role if the stored value isn't in their current membership list — that
 *     handles the case where an admin removed them from the role between
 *     sessions.
 *   - Writes to localStorage on every change.
 *   - Per-user keying (`df.lastRole.<login>`) so the preference survives a
 *     different user logging in on the same browser without pollution.
 */
const STORAGE_PREFIX = 'df.lastRole.';

function readStored(login: string): number | null {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX + login);
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeStored(login: string, roleId: number | null): void {
  try {
    if (roleId == null) localStorage.removeItem(STORAGE_PREFIX + login);
    else localStorage.setItem(STORAGE_PREFIX + login, String(roleId));
  } catch {
    // localStorage disabled / full — non-fatal
  }
}

export function useRememberedRole(): [number | null, (r: number | null) => void] {
  const user = useAuthStore(s => s.user);
  const myRoles = useMyRoles();

  // useState's initial value is read once; if user hasn't loaded yet this
  // returns null and the effect below patches it in once roles arrive.
  const initial = useMemo(() => (user ? readStored(user.login) : null), [user?.login]);
  const [role, setRoleState] = useState<number | null>(initial);

  // When the user changes (e.g. after a sign-in/out cycle), re-hydrate.
  useEffect(() => {
    if (!user) return;
    const stored = readStored(user.login);
    if (stored != null) setRoleState(stored);
  }, [user?.login]);

  // Once we know the user's actual role list, validate the current selection.
  useEffect(() => {
    if (!myRoles.data || myRoles.data.length === 0) return;
    if (role != null && myRoles.data.some(r => r.id === role)) return;
    setRoleState(myRoles.data[0]?.id ?? null);
  }, [myRoles.data, role]);

  function setRole(r: number | null) {
    setRoleState(r);
    if (user?.login) writeStored(user.login, r);
  }

  return [role, setRole];
}
